import { config } from "@/config/axios/config";
import { getAccessToken, getTenantId } from "@/utils/auth";
import { ElMessage } from "element-plus";
import errorCode from "./errorCode";
import { i18n } from "@/plugins/vueI18n";

// 补充类型定义
declare global {
  interface Window {
    $t?: (key: string) => string;
  }
}

const { base_url, result_code } = config;
const tenantEnable = import.meta.env.VITE_APP_TENANT_ENABLE;

// 定义请求方法类型
type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | string;

// 定义流式请求配置类型
export interface StreamRequestOptions<T = any> {
  url: string;
  method?: HttpMethod;
  data?: T;
  params?: Record<string, any>;
  headers?: Record<string, string>;
}

/**
 * 流式请求封装（兼容现有请求拦截器逻辑）
 * @param option 请求配置
 * @param onChunk 接收分片回调
 * @param onComplete 完成回调
 * @param onError 错误回调
 * @returns AbortController 用于取消请求
 */
export function streamRequest<T = any>(
  option: StreamRequestOptions<T>,
  onChunk: (chunk: string) => void,
  onComplete: () => void,
  onError: (error: string) => void,
): AbortController {
  const controller = new AbortController();
  const { signal } = controller;

  // 🌟 修复：使用全局i18n实例，而非在非setup环境调用useI18n
  const t = i18n.global.t || ((key: string) => key);

  // 构建请求URL
  let requestUrl = base_url + option.url;
  if (option.method?.toUpperCase() === "GET" && option.params) {
    const paramsStr = new URLSearchParams(option.params).toString();
    if (paramsStr) {
      requestUrl += "?" + paramsStr;
    }
  }

  // 构建请求头
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...option.headers,
  };

  // Token处理
  const isToken = headers.isToken !== false;
  if (getAccessToken() && isToken) {
    headers.Authorization = "Bearer " + getAccessToken();
  }

  // 租户信息处理
  if (tenantEnable && tenantEnable === "true") {
    const tenantId = getTenantId();
    if (tenantId) {
      headers["tenant-id"] = tenantId;
    }
  }

  // 发送请求
  fetch(requestUrl, {
    method: option.method || "POST",
    headers,
    body: option.method?.toUpperCase() !== "GET" ? JSON.stringify(option.data) : undefined,
    signal,
  })
    .then(async (response) => {
      if (!response.ok) {
        let errorMsg = t("sys.api.apiRequestFailed") + response.status;
        try {
          const errorData = await response.json();
          const code = errorData.code || result_code;
          errorMsg = errorData.msg || errorCode[code] || errorCode["default"] || errorMsg;
        } catch (e) {
          /* 非JSON响应 */
        }
        throw new Error(errorMsg);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder("utf-8");

      if (!reader) {
        throw new Error(t("sys.api.errMsg500"));
      }

      // 类型安全的流读取逻辑
      const readStream = async () => {
        try {
          const result: ReadableStreamReadValueResult<Uint8Array> | ReadableStreamReadDoneResult<Uint8Array> =
            await reader.read();

          if (result.done) {
            onComplete();
            return;
          }

          if (result.value && result.value instanceof Uint8Array) {
            const chunk = decoder.decode(result.value, { stream: true });
            onChunk(chunk);
          }

          readStream();
        } catch (e) {
          if (!signal.aborted) {
            onError(e instanceof Error ? e.message : t("sys.api.unknownError"));
          }
        }
      };

      readStream();
    })
    .catch((error) => {
      if (signal.aborted) return;
      const errorMsg = error instanceof Error ? error.message : t("sys.api.unknownError");
      onError(errorMsg);
      ElMessage.error(errorMsg);
    });

  return controller;
}

/**
 * 解析Dify格式的流式数据
 * @param chunk 原始流数据
 * @param onMessage 解析后的消息回调
 * @param onDone 完成回调
 */
export function parseDifyStream(chunk: string, onMessage: (text: string) => void, onDone: () => void) {
  const lines = chunk.split("\n");
  if (lines != null && lines.length > 0) {
    console.info("lines:", chunk);
    console.info("lines:", lines);
  }
  for (const line of lines) {
    if (line.startsWith("data: ")) {
      try {
        const jsonStr = line.substring(6).trim();
        if (jsonStr === "[DONE]" || jsonStr === "[workflow_finished]") {
          onDone();
          continue;
        }
        if (!jsonStr) continue;
        const parsed = JSON.parse(jsonStr);
        if (parsed.event === "message" && parsed?.answer) {
          // agent形式
          onMessage(parsed.answer);
        }
        if (parsed.event === "text_chunk" && parsed?.data) {
          // 工作流形式
          onMessage(parsed.data.text);
        } else if (parsed.event === "done" || parsed.event === "workflow_finished") {
          // agent形式||工作流形式
          onDone();
        }
      } catch (e) {
        console.warn("解析Dify流失败:", line, e);
      }
    } else {
      // {"code":"invalid_param","message":"(type 'text-input') age in input form must be a string","status":400}
      try {
        const maybeError = JSON.parse(line.trim());
        if (maybeError.code && maybeError.message) {
          // [状态]：${maybeError.status} ；
          onMessage(`【错误】 [代码]：${maybeError.code} ；[内容]：${maybeError.message}`);
          onDone();
          return; // 或 break，根据需求
        }
      } catch {
        // 不是有效 JSON，忽略
      }
    }
  }
}

// 补充缺失的类型定义
interface ReadableStreamReadResult<T> {
  done: boolean;
  value: T | undefined;
}
